import { formatHistoricalDate } from "rant-utils";
import { api } from "../../api/api";
import { AutoForm } from "../../core/form/AutoForm";
import { Dropdown } from "../../core/form/Dropdown";
import { Static } from "../../core/form/Static";
import { Text } from "../../core/form/Text";
import { useApi } from "../../core/hooks/useApi";
import { DataContext, useDataContext } from "../../core/hooks/useData";
import { hasRole, isImpersonating, useSession } from "../../core/hooks/useSession";
import { DataAwareButton } from "../../core/ui/DataAwareButton";
import { newNotification } from "../../core/ui/Notification";
import { Spinner } from "../../core/ui/Spinner";
import { Table } from "../../core/ui/Table";
import { Icon } from "../../core/ui/icons";
import { validateEmail } from "../../core/utils/validate";
import { account_invite } from "../../types";
import { Page } from "../Page";
import { go } from "../../core/route/Route";
import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "../../core/hooks/useDebounce";
import { closestKey } from "../../core/utils/dom";
import { useConditional } from "../../core/hooks/useConditional";
import { FormModal } from "../../data-entry/FormModal";


export interface RefTableItem {
	id: string,
	title: string,
}

export function useRefTableItems(name: string): RefTableItem[] {

	const result = useApi({
		request: () => {
			return api.ref_get({
				ids: ["package-type"]
			});
		},

		prepareResult: (result: any) => {
			if (result && result.data) {
				const items = [];
				for(let r of result.data.rows) {
					items.push({
						id: r["0"],
						title: r["1"],
					});
				}
				return items;
			}
		},

	}, [name]);

	return result as RefTableItem[];
}

export function Profile(props: {

}) {

	const context = useDataContext({ 
		initialValue: {
		} as any,
	});

	const user = useApi({
		request: () => api.user_get(),
		data: context,
	}, []);

	const packageTypes = useRefTableItems("package-type");

	return (
		<DataContext.Provider value={context}>
			<Page
				compact={true}
			
				title={{
					text: "My Account",
					secondary: context.value.email, //<Static prop="email" />,
					icon: "employee_tag_color",

				primaryButton: <DataAwareButton
					onClick={() => {
						const v = context.value;

						const data = {
							name: v.name,
							mobile: v.mobile,
							defaults: v.defaults,
							account: v.account,
						};

						api.user_post(data).then(() => {
							// clear out any cached defaults stored in localsession
							localStorage.setItem("defaults", "");
							localStorage.setItem("account", data.account);

							go("/dashboard/");

						}).catch(() => {

						});
					}}
				>Save</DataAwareButton>
			}}>

				<div className="flex vert gap-10 align-start" style={{marginRight:"40px"}}>

					<div className="flex horiz align-start gap-10">
						<div className="flex vert gap-10 align-start">
							<fieldset>
								<legend>Name</legend>

								<AutoForm fields={[
									{ prop: "name.given1", title: "Given Name", options: { required: true } },
									{ prop: "name.family", title: "Family Name", options: { required: true } },
								]} />

							</fieldset>
						</div>

						<div className="flex vert gap-10">
							<fieldset>
								<legend>Phone</legend>

								<AutoForm fields={[
									{ prop: "mobile", title: "Mobile" },
								]} />

							</fieldset>

						</div>

						{/* <div>
							<fieldset>
								<legend>Address</legend>

								<AutoForm fields={[
									{ prop: "careOf", title: "Care Of" },
									{ prop: "line2", title: "Room / Floor / Building / PO Box" },
									{ prop: "street", title: "Street Address" },
									{ prop: "suburb", title: "Suburb / Town" },
									{ prop: "state", title: "State" },
									{ prop: "postCode", title: "Post Code" },
									{ prop: "country", title: "Country" },
								]} />

							</fieldset>
						</div> */}
					</div>

					<div className="flex horiz align-start gap-10">
						<fieldset className="width-50">
							<legend>My Access</legend>

							<MyAccess />
						</fieldset>

						<fieldset>
							<legend>Defaults</legend>

							<AutoForm fields={[
								{ 
									prop: "defaults.package",
									title: "Package", 
									options: {
										controlType: "dropdown",
										items: packageTypes,
										getValue: (o: any) => {
											return o ? o.id : "";
										},
										getDisplay: (o: any) => {
											return o ? o.title : "";
										},
									}
								},
							]} />

						</fieldset>

					</div>

				</div>

				{
					hasRole("admin") &&
					<div>
						<fieldset className="width-100pc">
							<legend>Impersonate</legend>
							<Impersonate />
							<NewAccount />
						</fieldset>
					</div>
				}

				{
					<ClientOptions />
				}
				
			</Page>
		</DataContext.Provider>
	);
}

export function MyAccess(props: {

}) {

	const accounts = useApi({
		request: () => api.user_accounts(),
	}, []) as any[];

	const session = useSession();

	const dataContext = useDataContext({ initialValue: { 
		account: session.authToken!.account,
		inviteEmail: "",
		inviteName: "",
	} })

	const items = [] as any[];

	if (!accounts) {
		items.push(
			<Spinner size="small" key="spinner" />,
		)
	} else {
		if (accounts && accounts.length > 1) {
			items.push(
				<div className="flex horiz align-end" key="change-account">
					<Dropdown
						items={accounts}
						getDisplay={(item, selected) => {
							return item.account.name;
						}}
						getValue={(item) => {
							return item.account.id;
						}}
						prop="account"
						
					/>
					<button
						onClick={() => {
							api.user_change_account({ toAccount: dataContext.value.account }).then(() => {
								// go back to dashboard
								document.location.href = "/dashboard/";

							}).catch(() => {

							})
						}}
					>Change Account</button>
				</div>
			);
		}
	}
 
	return (
		<DataContext.Provider value={dataContext} key="selection">
			<div className="flex vert gap-10 align-start">
				{items}

				{
					hasRole("account-owner") && 
					<div className="">
						<div>Invite another person to join your account</div>
						<div>
							<Text
								title="Name of person to invite"
								prop="inviteName"
							/>
							<Text
								title="Email address"
								prop="inviteEmail"
							/>
							<button
								className="primary"
								onClick={() => {
									const email = dataContext.value.inviteEmail;
									const name = dataContext.value.inviteName;

									if (!name) {
										newNotification({
											title: "Name is required",
											message: "Please enter the name of the person you are inviting and try again.",
											style: "error",
											type: "modal",										
										})
										return;
									}
									if (!validateEmail(email)) {
										newNotification({
											title: "Email address not valid",
											message: "Please check you have typed the email address correctly and try again.",
											style: "error",
											type: "modal",										
										})
										return;
									}

									api.account_invite({
										email,
										name,
									}).then(() => {

									}).catch(() => {

									})

									newNotification({
										title: "Invitation sent",
										message: 
											<div className="flex vert gap-10 align-center">
												<div>An email has been sent to <b>{email}</b> inviting them to join this account.</div>
												<Icon type="email_send_color" />
											</div>,
										style: "success",
										type: "modal",										
									});

									dataContext.setValue("inviteEmail", "");
									dataContext.setValue("inviteName", "");
								}}
							>Send Invitation</button>
						</div>
					</div>
				}

				{
					hasRole("account-owner") && 
					<Invites />
				}
			</div>
		</DataContext.Provider>
	);

}

export function Invites(props: {

}) {

	const invites = useApi<account_invite[]>({
		request: () => api.account_invite_get_all({}),		
	}, []);

	if (!invites) return null;

	return (
		<Table
			noItemsMesssage="There have not been any invitations sent to other people yet."
			cols={[
				{ id: "email", name: "Invitation sent to", },
				{ id: "name", name: "Name", },
				{ id: "created", name: "Sent", },
				{ id: "accepted", name: "Accepted", },
			]}
			items={invites}
			render={{
				colTitle: (c) => c.name,
				colValue: (r: any, c) => {
					if (c.id === "created") {
						return <div className="tag subtle">{formatHistoricalDate(r.created)}</div>;
					} else if (c.id === "accepted") {
						if (r.dateAccepted) {
							return (
								<div className="tag secondary horiz align-center gap-2 padding-2" style={{display: "flex"}}>
									<Icon type="tick" color="white" size="small" />
									<span>{formatHistoricalDate(r.dateAccepted)}</span>
								</div>
							)
						}
					} else {
						return r[c.id];
					}
				},
			}}

		/>
	)
}

export function Impersonate(props: {

}) {

	const [filter, setFilter] = useDebounce(300, "");
	const [results, setResults] = useState([] as any[]);

	useEffect(() => {
		if (!filter.trim()) return;

		api.user_search_all({
			filter,
		}).then((resp: any) => {
			const auMap = {} as any;
			const aMap = {} as any;
			const uMap = {} as any;

			for(let a of resp.accounts) {
				if (!aMap[a.id]) {
					aMap[a.id] = a;
					a.users = [];
				}
			}
			for(let u of resp.users) {
				if (!uMap[u.id]) uMap[u.id] = u;
			}

			for(let au of resp.accountUsers) {
				const a = aMap[au.account]				;
				a.users.push(uMap[au.user]);
			}

			const results = [];
			for(let m in aMap) {
				results.push(aMap[m]);
			}
			setResults(results);
			
		}).catch((e) => {

		});

	}, [filter]);

	function handleImpersonate(e: any) {
		const key = closestKey(e.target, ".impersonate");
		if (!key) return;

		api.auth_impersonate({
			user: key,
		}).then((resp) => {
			document.location.href = "/dashboard/";
		}).catch((e) => {

		});
	}

	const items = [];
	for(let a of results) {
		const users = [];
		for(let u of a.users) {
			users.push(
				<li>
					{u.name.given1} {u.name.family}
					<button 
						className="inline impersonate"
						data-key={u.id}
						onClick={handleImpersonate}
					>Login</button>
				</li>
			)
		}

		items.push(
			<div className="margin-top-5">
				<b>{a.name}</b>
				{users}
			</div>
		);
	}


	return (
		<div>
			<Text prop="qry" onChange={(e, value) => {
				setFilter(value);
			}} />

			{items}
		</div>
	)

}

export function ClientOptions(props: {

}) {

	const fields = [
		{ prop: "account.name", title: "Account name" },
		{ prop: "account.regOffice", title: "Registered office", options: {
			controlType: "address"
		} },
	] as any[];

	if (isImpersonating() || hasRole("admin")) fields.push(
		{ prop: "account.options.enduringCard", title: "Enduring credit card", options: { controlType: "checkbox" } },
		{ prop: "account.options.extendedCredit", title: "Extended credit", options: { controlType: "checkbox" } },
	);

	return (
		<fieldset className="width-50pc">
			<legend>Client Options</legend>

			<AutoForm
				fields={fields}
			/>
		</fieldset>
	);
}

export function NewAccount(props: {

}) {

	const data = useDataContext({
		initialValue: {

		}
	});

	const modal = useConditional(() => {

		return (
			<FormModal
				title="Create account"
				form="admin-account-create"
				onCancel={modal.close}
				onSave={(value) => {
					debugger;
				}}
			/>


			// <Modal
			// 	title="Create Account"
			// 	size="width-50vw"
			// 	onCancel={modal.close}
			// 	onSave={() => {

			// 	}}
			// >

			// </Modal>
		)
	});

	return (
		<DataContext.Provider value={data}>
			<div className="margin-top-5">
				<button className="primary"
					onClick={() => {
						modal.show({});
					}}
				>Create Account</button>
				{modal.render()}
			</div>
		</DataContext.Provider>
	)
}